import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Box,
  Stack,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  useTheme,
  Autocomplete,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AddAlarm } from '@mui/icons-material';
import { debounce } from 'lodash-es';
import { OfferListItem, OfferSchedule, OfferScheduleDTO, ScheduleType } from '@modules/offers/types';
import { postOfferSchedule, getOffers } from '@modules/offers/services/api';
import { handleSuccessNotification, handleErrorNotification } from '@core/utils/notifications';
import ConfirmationDialog from '@shared/components/dialogs/ConfirmationDialog';
import { DrawerContent, DrawerFooter, DrawerHeader } from '@shared/components/drawer';
import { OfferBasicInfo } from '../../shared/components/Cards/OfferBasicInfo';

interface ScheduleDrawerProps {
  open: boolean;
  onClose: () => void;
  offer?: Partial<OfferListItem> | null;
  offerId?: number;
  mode?: 'listing' | 'inline';
  initialSchedule?: OfferScheduleDTO | OfferSchedule | null;
  onInlineSave?: (schedule: OfferScheduleDTO) => void;
  bulkMode?: boolean;
  offerOptions?: OfferListItem[];
}

export const ScheduleDrawer: React.FC<ScheduleDrawerProps> = ({
  open,
  onClose,
  offer,
  offerId,
  mode = 'listing',
  initialSchedule,
  onInlineSave,
  bulkMode = false,
  offerOptions = [],
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<{
    offer_schedule_id?: number;
    schedule_type: ScheduleType;
    entry_name: string;
    start_date: Date | null;
    end_date: Date | null;
    is_active: boolean;
  }>(() => ({
    offer_schedule_id: initialSchedule?.offer_schedule_id,
    schedule_type: initialSchedule?.schedule_type || 'Date',
    entry_name: initialSchedule?.entry_name || '',
    start_date: initialSchedule?.start_date ? new Date(initialSchedule.start_date) : null,
    end_date: initialSchedule?.end_date ? new Date(initialSchedule.end_date) : null,
    is_active: initialSchedule?.is_active ?? true,
  }));

  const [selectedOffers, setSelectedOffers] = useState<OfferListItem[]>([]);
  const [options, setOptions] = useState<OfferListItem[]>(offerOptions?.slice(0, 10) || []);
  const [search, setSearch] = useState<string>('');
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset form when initial values change
  useEffect(() => {
    setForm({
      offer_schedule_id: initialSchedule?.offer_schedule_id,
      schedule_type: initialSchedule?.schedule_type || 'Date',
      entry_name: initialSchedule?.entry_name || '',
      start_date: initialSchedule?.start_date ? new Date(initialSchedule.start_date) : null,
      end_date: initialSchedule?.end_date ? new Date(initialSchedule.end_date) : null,
      is_active: initialSchedule?.is_active ?? true,
    });
  }, [initialSchedule]);

  // Debounced remote search for offers
  const debouncedFetchOffers = useMemo(
    () =>
      debounce(async (q: string) => {
        try {
          setLoadingOptions(true);
          const res = await getOffers({ page: 1, limit: 10, search: q });
          setOptions(res.data.data || []);
        } catch (e) {
          // ignore fetch error here; UI can keep previous options
          console.error('Failed to fetch offers for schedule drawer:', e);
        } finally {
          setLoadingOptions(false);
        }
      }, 300),
    [],
  );

  useEffect(() => {
    if (!bulkMode) return;
    debouncedFetchOffers(search);
    return () => {
      debouncedFetchOffers.cancel();
    };
  }, [bulkMode, search, debouncedFetchOffers]);

  const mutation = useMutation({
    mutationFn: (payload: OfferScheduleDTO) => postOfferSchedule(payload),
    onSuccess: (data) => {
      handleSuccessNotification(data, 'Schedule saved successfully');
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      onClose();
    },
    onError: (error) => {
      handleErrorNotification(error, 'Failed to save schedule');
    },
  });

  const canSave =
    form.entry_name.trim() && form.start_date && form.end_date && (!bulkMode || selectedOffers.length > 0);

  const handleSave = async () => {
    if (!canSave) return;
    const ids = bulkMode ? selectedOffers.map((o) => Number(o.offer_id)) : [Number(offer?.offer_id ?? offerId ?? 0)];
    const payload: OfferScheduleDTO = {
      offer_schedule_id: form.offer_schedule_id,
      offer_id: ids,
      schedule_type: form.schedule_type,
      entry_name: form.entry_name.trim(),
      start_date: form.start_date.toISOString(),
      end_date: form.end_date.toISOString(),
      is_active: form.is_active,
    };
    if (mode === 'inline') {
      onInlineSave?.(payload);
      onClose();
    } else {
      await mutation.mutateAsync(payload);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{ paper: { sx: { width: { xs: '100%', md: 480 } } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <DrawerHeader
            title={bulkMode ? 'Bulk Schedule Configuration' : 'Configure Schedule'}
            onClose={onClose}
            icon={bulkMode ? <AddAlarm /> : undefined}
          />

          <DrawerContent>
            <Stack spacing={4}>
              {!bulkMode && offer && <OfferBasicInfo offer={offer} />}
              {bulkMode && (
                <Autocomplete
                  multiple
                  options={options}
                  filterOptions={(opts) => {
                    // Filter out already selected offers
                    const selectedIds = new Set(selectedOffers.map((o) => String(o.offer_id)));
                    return opts.filter((opt) => !selectedIds.has(String(opt.offer_id)));
                  }}
                  loading={loadingOptions}
                  disableCloseOnSelect
                  getOptionLabel={(o) => `${o.offer_name} (#${o.offer_id})`}
                  isOptionEqualToValue={(o, v) => String(o.offer_id) === String(v.offer_id)}
                  value={selectedOffers}
                  onChange={(_, v) => setSelectedOffers(v)}
                  onInputChange={(_, v) => setSearch(v)}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Offers" placeholder="Search by name or id" />
                  )}
                  loadingText="Loading offers..."
                  noOptionsText={search ? 'No offers found for search' : 'Type to search offers'}
                />
              )}
              <TextField
                label="Schedule Name"
                value={form.entry_name}
                onChange={(e) => setForm((p) => ({ ...p, entry_name: e.target.value }))}
                fullWidth
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <DateTimePicker
                    label="Start"
                    sx={{ p: 0 }}
                    value={form.start_date}
                    onChange={(d) => setForm((p) => ({ ...p, start_date: d }))}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          '& .MuiInputLabel-root': {
                            top: 3,
                            left: 0,
                          },
                          '& .MuiPickersInputBase-root': {
                            height: 41,
                          },
                        },
                      },
                    }}
                  />
                  <DateTimePicker
                    label="End"
                    sx={{ p: 0 }}
                    value={form.end_date}
                    onChange={(d) => setForm((p) => ({ ...p, end_date: d }))}
                    minDateTime={form.start_date || undefined}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          '& .MuiInputLabel-root': {
                            top: 3,
                            left: 0,
                          },
                          '& .MuiPickersInputBase-root': {
                            height: 41,
                          },
                        },
                      },
                    }}
                  />
                </Stack>
              </LocalizationProvider>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <TextField
                  select
                  label="Schedule Type"
                  value={form.schedule_type}
                  onChange={(e) => setForm((p) => ({ ...p, schedule_type: e.target.value as ScheduleType }))}
                  sx={{ maxWidth: 240 }}
                >
                  <MenuItem value="Date">One Time (Date/Range)</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                </TextField>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_active}
                      onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                    />
                  }
                  label="Auto Scheduler"
                />
              </Stack>

              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Configure a one-time, weekly, monthly, or specific day schedule with precise start/end date & time.
              </Typography>
            </Stack>
          </DrawerContent>

          <DrawerFooter
            primaryButton={{
              label: 'Save Schedule',
              onClick: () => (bulkMode ? setConfirmOpen(true) : handleSave()),
              loading: mutation.isPending,
              disabled: !canSave,
            }}
          />
        </Box>
      </Drawer>

      {/* Bulk override confirmation */}
      {bulkMode && (
        <ConfirmationDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={async () => {
            setConfirmOpen(false);
            await handleSave();
          }}
          title="Override existing schedules?"
          message="This action will override any existing schedules for the selected offers. Do you wish to continue?"
          confirmText="Confirm"
          cancelText="Cancel"
          variant="warning"
          confirmColor="primary"
        />
      )}
    </>
  );
};
